import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';

import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { clearServiceWorkerUserCaches } from '../../serviceWorkerRegistration';
import { memberFetch } from '../../services/memberApi';

import CustomerDashboard from './CustomerDashboard';
import { MemberPreferencesProvider } from './preferences/MemberPreferencesContext';
import { MemberWhatsNewProvider } from './release-notes/MemberWhatsNewContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const CustomerPortal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [member, setMember] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(null); // null | { reason }

  useEffect(() => {
    const verifyAuth = async () => {
      // Support both new memberToken and legacy customerToken
      const token = localStorage.getItem('memberToken') || localStorage.getItem('customerToken');
      const memberData = localStorage.getItem('memberData');

      if (!token) {
        navigate('/account/login');
        return;
      }

      // Parse stored member data
      let storedMemberData = null;
      if (memberData) {
        try {
          storedMemberData = JSON.parse(memberData);
        } catch (e) {
          console.error('Failed to parse member data:', e);
        }
      }

      const authType = storedMemberData?.auth_type || 'customer';
      const permissions = storedMemberData?.permissions || { can_view_billing: true, can_view_siblings: true };
      const isImpersonation = storedMemberData?.is_impersonation || false;

      // For impersonation sessions, use stored data (just verified by ImpersonationVerify)
      if (isImpersonation && storedMemberData?.entity) {
        setMember({
          ...storedMemberData.entity,
          auth_type: storedMemberData.auth_type,
          permissions,
          is_impersonation: true,
          impersonated_by: storedMemberData.impersonated_by
        });
        setLoading(false);
        return;
      }

      // For students, we don't call /api/customer/me - use stored data
      if (authType === 'student') {
        if (storedMemberData?.entity) {
          setMember({
            ...storedMemberData.entity,
            auth_type: 'student',
            permissions
          });
          setLoading(false);
          return;
        } else {
          // No stored student data - need to re-login
          handleLogout();
          return;
        }
      }

      // For customers, verify token and fetch fresh data
      try {
        const response = await fetch(`${API_BASE}/api/customer/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setMember({
            ...data,
            auth_type: 'customer',
            permissions
          });
          localStorage.setItem('customerData', JSON.stringify(data));
        } else {
          // Token invalid or expired
          handleLogout();
        }
      } catch (err) {
        // Network error - try to use cached data
        const cachedData = localStorage.getItem('customerData');
        if (cachedData) {
          setMember({
            ...JSON.parse(cachedData),
            auth_type: 'customer',
            permissions
          });
        } else {
          navigate('/account/login');
        }
      } finally {
        setLoading(false);
      }
    };

    verifyAuth();
  }, [navigate]);

  // Refresh member data when tab becomes visible (catches account status changes)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && member) {
        refreshMember();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [member]);

  // Surface mid-session 401s as a re-login prompt instead of silent API failures (OWZ-13)
  useEffect(() => {
    const handler = (event) => {
      setSessionExpired({ reason: event?.detail?.reason || 'expired' });
    };
    window.addEventListener('member:session-expired', handler);
    return () => window.removeEventListener('member:session-expired', handler);
  }, []);

  const handleLogout = () => {
    clearServiceWorkerUserCaches('members');
    localStorage.removeItem('customerToken');
    localStorage.removeItem('customerData');
    localStorage.removeItem('memberToken');
    localStorage.removeItem('memberData');
    navigate('/account/login');
  };

  const refreshMember = async () => {
    const token = localStorage.getItem('memberToken') || localStorage.getItem('customerToken');
    if (!token) return;

    // Don't refresh for impersonation sessions or students (they don't have a /me endpoint)
    const memberData = JSON.parse(localStorage.getItem('memberData') || '{}');
    if (memberData?.is_impersonation) return;
    if (member?.auth_type === 'student') return;

    try {
      // memberFetch dispatches the session-expired event automatically on 401
      const response = await memberFetch('/api/customer/me');
      if (response.ok) {
        const data = await response.json();
        setMember(prev => ({
          ...data,
          auth_type: prev?.auth_type || 'customer',
          permissions: prev?.permissions || { can_view_billing: true, can_view_siblings: true }
        }));
        localStorage.setItem('customerData', JSON.stringify(data));
      }
    } catch (err) {
      console.error('Failed to refresh member data:', err);
    }
  };

  const handleSessionExpiredConfirm = () => {
    setSessionExpired(null);
    handleLogout();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600 dark:text-gray-400" />
      </div>
    );
  }

  return (
    <>
      <MemberPreferencesProvider memberId={member?.id} authType={member?.auth_type}>
        <MemberWhatsNewProvider>
          <CustomerDashboard
            member={member}
            onLogout={handleLogout}
            onRefresh={refreshMember}
          />
        </MemberWhatsNewProvider>
      </MemberPreferencesProvider>
      <Dialog
        open={sessionExpired !== null}
        onOpenChange={(open) => { if (!open) handleSessionExpiredConfirm(); }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Your session has expired
            </DialogTitle>
            <DialogDescription>
              {sessionExpired?.reason === 'inactivity'
                ? 'You were signed out due to inactivity. Please log in again to continue.'
                : 'Your session has expired. Please log in again to continue.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={handleSessionExpiredConfirm}
              className="bg-[#9B1B30] hover:bg-[#7d1626] text-white"
            >
              Log in again
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomerPortal;
